import { useToastStore } from '@/domains/notifications/store/use-toast-store/useToastStore'
import { toRequestPath } from '@/utils/url-util/urlUtil'

interface ServerErrorReport {
  status: number
  url?: string
  body?: unknown
}

/**
 * The one server error whose `message` is written for the user, as 409 is in `Handler.php`.
 * A 500's message, where Laravel shows one, was written for a developer.
 */
const SERVICE_UNAVAILABLE = 503

// Laravel's 503 message while the app is down (`PreventRequestsDuringMaintenance`).
const MAINTENANCE_MESSAGE = 'Service Unavailable'

/**
 * A 5xx status is the server admitting fault. Every other failure — a validation error,
 * a permission denial, a missing record — is an answer the screen already handles.
 */
export const isServerError = (status?: number): status is number =>
  status !== undefined && status >= 500 && status <= 599

/**
 * The sentence a 503 wrote for the user, such as a report that ran out of time. An HTML
 * error page has no `message`, and a dropped connection has no body.
 */
const serverSentence = (status: number, body: unknown): string | undefined => {
  if (status !== SERVICE_UNAVAILABLE) {
    return undefined
  }

  const message = typeof body === 'object' && body !== null && 'message' in body ? body.message : undefined
  const isWritten = typeof message === 'string' && message.trim() !== '' && message !== MAINTENANCE_MESSAGE

  return isWritten ? message : undefined
}

/**
 * Show a notification that only the user can dismiss.
 *
 * A server error can land while the user is looking at another part of the screen, and an
 * expiring toast lets it pass unseen. The `sticky` state gives the toast no `life`, so it
 * waits on screen until the user closes it.
 */
export const reportServerError = ({ status, url, body }: ServerErrorReport): void => {
  const path = toRequestPath(url)
  const toastStore = useToastStore()

  // One notification per status and path. A broken screen can fire several requests at the
  // same endpoint at once, and every sticky toast has to be closed by hand.
  const id = `server-error-${status}-${path}`

  // A sentence the server wrote explains a stop, not a crash, so its heading does not say "error".
  const sentence = serverSentence(status, body)
  const copy = sentence
    ? { summary: 'Could not finish', detail: sentence }
    : {
        summary: 'Server error',
        detail: `The server returned a ${status} error for ${path}. Try again. Tell support if it repeats.`
      }

  toastStore.clearOperation(id)

  toastStore.add({
    id,
    state: 'sticky',
    severity: 'error',
    closable: true,
    ...copy
  })
}
